import type { paths } from "@/api-schema";
import { inputStringToDate } from "@/common/helpers/date-input";
import { formatDateToApi } from "@/common/helpers/format-date";
import { DateFormat } from "@/store/user-config-store";

type VehicleRequestBody = paths["/v1/vehicle/search"]["post"]["requestBody"]["content"]["application/json"];
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function toIsoDateOrUndefined(value: string | undefined, dateFormat: DateFormat): string | undefined {
  if (!value) {
    return undefined;
  }

  if (ISO_DATE_REGEX.test(value)) {
    return value;
  }

  const parsedDate = inputStringToDate(value, { dateFormat });
  if (!parsedDate) {
    return undefined;
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function buildVehicleSearchBody({
  externalBody,
  filterValues,
  dateFormat,
  nowDate = new Date(),
}: {
  externalBody: VehicleRequestBody | undefined;
  filterValues: Record<string, string[]>;
  dateFormat: DateFormat;
  nowDate?: Date;
}): VehicleRequestBody {
  const registrationDateFrom = toIsoDateOrUndefined(filterValues.registrationDateRange?.[0], dateFormat);
  const explicitRegistrationDateTo = toIsoDateOrUndefined(filterValues.registrationDateRange?.[1], dateFormat);
  const registrationDateTo =
    explicitRegistrationDateTo ?? (registrationDateFrom ? formatDateToApi(nowDate) : undefined);

  return {
    ...externalBody,
    ...(filterValues.branches?.length > 0 && { currentBranch: filterValues.branches }),
    ...(filterValues.brands?.length > 0 && { brand: filterValues.brands }),
    ...(filterValues.modelFamilies?.length > 0 && { modelFamily: filterValues.modelFamilies }),
    ...(filterValues.modelGenerations?.length > 0 && { modelGeneration: filterValues.modelGenerations }),
    ...(filterValues.blockedVehicles?.length > 0 &&
      filterValues.blockedVehicles[0] === "blocked" && { isBlocked: true }),
    ...(filterValues.blockedVehicles?.length > 0 &&
      filterValues.blockedVehicles[0] === "unblocked" && { isBlocked: false }),
    ...(filterValues.vehicleStatus?.length > 0 && {
      status: filterValues.vehicleStatus as VehicleRequestBody["status"],
    }),
    ...(filterValues.fuelType?.length > 0 && {
      fuelType: filterValues.fuelType as VehicleRequestBody["fuelType"],
    }),
    ...(filterValues.vehicleType?.length > 0 && {
      vehicleType: filterValues.vehicleType as VehicleRequestBody["vehicleType"],
    }),
    ...(filterValues.vehicleKind?.length > 0 && {
      vehicleKind: filterValues.vehicleKind as VehicleRequestBody["vehicleKind"],
    }),
    ...(registrationDateFrom && { registrationDateFrom }),
    ...(registrationDateTo && { registrationDateTo }),
    ...(filterValues.mileageRange?.[0] && { mileageMin: Number.parseInt(filterValues.mileageRange[0], 10) }),
    ...(filterValues.mileageRange?.[1] && { mileageMax: Number.parseInt(filterValues.mileageRange[1], 10) }),
    ...(filterValues.powerKwRange?.[0] && { powerKwMin: Number.parseInt(filterValues.powerKwRange[0], 10) }),
    ...(filterValues.powerKwRange?.[1] && { powerKwMax: Number.parseInt(filterValues.powerKwRange[1], 10) }),
    ...(filterValues.powerPsRange?.[0] && { powerPsMin: Number.parseInt(filterValues.powerPsRange[0], 10) }),
    ...(filterValues.powerPsRange?.[1] && { powerPsMax: Number.parseInt(filterValues.powerPsRange[1], 10) }),
  };
}
